"use client";
import { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  BookOpen,
  GraduationCap,
  Image as ImageIcon,
  Users,
  Clock,
  DollarSign
} from "lucide-react";

import { useCategories } from "../context/CategoryContext";
import { useCourseCategories } from "../context/CourseCategoryContext";
import { useLecturers } from "../context/LecturerContext";
import { useCourses } from "../context/CourseContext";
import { IMG_URL } from "../config/config";
import CourseContentModal from "./CourseContentModal.jsx";

const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
    purple: "bg-purple-50 text-purple-700 ring-purple-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

const SectionDivider = ({ label, right }) => (
  <div className="flex items-center gap-3 my-8">
    {label ? (
      <span className="inline-flex items-center rounded-full bg-white px-4 py-2 text-xs font-extrabold text-gray-800 ring-1 ring-gray-200 shadow-sm">
        {label}
      </span>
    ) : null}
    <div className="h-px flex-1 bg-gradient-to-r from-gray-200 via-gray-200 to-transparent" />
    {right ? <div className="flex items-center gap-2">{right}</div> : null}
  </div>
);

export default function Courses() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const { courses, fetchCourses, addCourse, updateCourse, deleteCourse, toggleCourseStatus } = useCourses();

  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const filteredCourses = courses.filter((course) =>
    course.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCourses.length / ITEMS_PER_PAGE);
  const start = (page - 1) * ITEMS_PER_PAGE;
  const currentCourses = filteredCourses.slice(start, start + ITEMS_PER_PAGE);

  const toggleStatus = (id, status) => {
    toggleCourseStatus(id, status);
  };

  // Add Course Handler
  const addCourseHandler = (data) => {
    const formData = new FormData();
    formData.append("type", data.type || "");
    formData.append("category_id", data.categoryId);
    formData.append("course_category_id", data.courseCategoryId);
    formData.append("lecturer_id", data.lecturerId);
    formData.append("name", data.name);
    formData.append("duration", data.duration);
    formData.append("mode", data.mode);
    formData.append("technologies", data.technologies);
    formData.append("certificate", data.certificate);
    formData.append("internship", data.internship);
    formData.append("price", data.price);
    formData.append("status", data.status);

    if (data.image instanceof File) {
      formData.append("image", data.image);
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    formData.append("create_audit_id", user.id);

    addCourse(formData);
    setShowAddModal(false);
  };

  // Update Course Handler
  const updateCourseHandler = (data) => {
    const formData = new FormData();
    formData.append("type", data.type || "");
    formData.append("category_id", data.categoryId);
    formData.append("course_category_id", data.courseCategoryId);
    formData.append("lecturer_id", data.lecturerId);
    formData.append("name", data.name);
    formData.append("duration", data.duration);
    formData.append("mode", data.mode);
    formData.append("technologies", data.technologies);
    formData.append("certificate", data.certificate);
    formData.append("internship", data.internship);
    formData.append("price", data.price);
    formData.append("status", data.status);

    // Only append image if a new file is selected
    if (data.image instanceof File) {
      formData.append("image", data.image);
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    formData.append("update_audit_id", user.id);

    updateCourse(data.id, formData);
    setShowEditModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb]">
      <div className="p-6 sm:p-8">
        {/* HERO SECTION */}
        <div className="rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden mb-8">
          <div className="relative p-7 sm:p-10">
            <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-red-200/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-orange-200/25 blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="red">
                    <GraduationCap className="h-4 w-4 mr-1" />
                    Course Management
                  </Pill>
                  <Pill tone="gray">Admin Portal</Pill>
                  <Pill tone="green">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Learning Platform
                  </Pill>
                </div>

                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900">
                  Courses
                </h1>
                <p className="mt-3 text-gray-600 text-sm sm:text-base">
                  Manage your course catalog with comprehensive tools for creating, editing, and organizing educational content.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-gray-600">Total</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {filteredCourses.length} courses
                  </div>
                </div>

                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-extrabold text-white hover:bg-red-700 transition shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  Add Course
                </button>
              </div>
            </div>
          </div>
        </div>

        <SectionDivider
          label="Browse Courses"
          right={
            <div className="flex items-center gap-2">
              {search && <Pill tone="amber" size="sm">Search: {search}</Pill>}
              <Pill tone="blue" size="sm">Page {page} of {totalPages}</Pill>
            </div>
          }
        />

        {/* SEARCH */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search courses..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Image
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Course Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Category
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Lecturer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {currentCourses.map((course, index) => (
                  <tr
                    key={course.id}
                    className={cx(
                      "hover:bg-gray-50 transition-colors",
                      index !== currentCourses.length - 1 ? "border-b border-gray-100" : ""
                    )}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {course.image ? (
                          <img
                            src={`${IMG_URL}/course/${course.image}`}
                            alt={course.name}
                            className="h-12 w-12 rounded-2xl object-cover ring-1 ring-gray-200"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-2xl bg-gray-100 ring-1 ring-gray-200 flex items-center justify-center">
                            <ImageIcon className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="font-extrabold text-gray-900">{course.name}</div>
                        <div className="flex items-center gap-2">
                          <Pill
                            tone={course.mode === "Online" ? "green" : course.mode === "Hybrid" ? "blue" : "purple"}
                            size="sm"
                          >
                            {course.mode}
                          </Pill>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs text-gray-600 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {course.duration}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">{course.main_category}</div>
                        <div className="text-xs text-gray-600">{course.category_name}</div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{course.lecturer_name}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          ₹{course.price}
                        </div>
                        <div className="flex items-center gap-2">
                          <Pill
                            tone={course.certificate === "Yes" ? "green" : "gray"}
                            size="sm"
                          >
                            Certificate: {course.certificate}
                          </Pill>
                        </div>
                        <div className="flex items-center gap-2">
                          <Pill
                            tone={course.internship === "Yes" ? "blue" : "gray"}
                            size="sm"
                          >
                            Internship: {course.internship}
                          </Pill>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleStatus(course.id, course.status)}
                        className="transition-all duration-200"
                      >
                        <Pill
                          tone={course.status === "Active" ? "green" : "yellow"}
                          size="sm"
                        >
                          {course.status}
                        </Pill>
                      </button>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedCourse(course);
                            setShowContentModal(true);
                          }}
                          className="p-2 rounded-2xl hover:bg-green-50 text-green-600 hover:text-green-700 transition-colors"
                          aria-label="Manage course content"
                        >
                          <BookOpen className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => {
                            setEditCourse(course);
                            setShowEditModal(true);
                          }}
                          className="p-2 rounded-2xl hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                          aria-label="Edit course"
                        >
                          <Edit className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => deleteCourse(course.id)}
                          className="p-2 rounded-2xl hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
                          aria-label="Delete course"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {currentCourses.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                          <BookOpen className="h-6 w-6 text-gray-400" />
                        </div>
                        <div>
                          <div className="font-extrabold text-gray-900">No courses found</div>
                          <p className="mt-1 text-sm text-gray-600">
                            {search ? "Try adjusting your search terms." : "Get started by adding your first course."}
                          </p>
                        </div>
                        {!search && (
                          <button
                            onClick={() => setShowAddModal(true)}
                            className="mt-2 inline-flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-sm font-extrabold text-white hover:bg-red-700 transition"
                          >
                            <Plus className="h-4 w-4" />
                            Add Course
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8">
            <button
              onClick={() => setPage(Math.max(page - 1, 1))}
              disabled={page === 1}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === 1
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={cx(
                    "px-4 py-2 rounded-2xl text-sm font-extrabold transition-colors",
                    pageNum === page
                      ? "bg-red-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-gray-200"
                  )}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => setPage(Math.min(page + 1, totalPages))}
              disabled={page === totalPages}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === totalPages
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* MODALS */}
        {showAddModal && (
          <CourseModal
            title="Add New Course"
            onClose={() => setShowAddModal(false)}
            onSave={addCourseHandler}
          />
        )}

        {showEditModal && editCourse && (
          <CourseModal
            title="Edit Course"
            course={editCourse}
            onClose={() => setShowEditModal(false)}
            onSave={updateCourseHandler}
          />
        )}

        {showContentModal && selectedCourse && (
          <CourseContentModal
            course={selectedCourse}
            onClose={() => {
              setShowContentModal(false);
              setSelectedCourse(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

// Modal Component
function CourseModal({ title, onClose, onSave, course }) {
  const { categories, fetchCategories } = useCategories();
  const { categories: courseCategories, fetchCourseCategories } = useCourseCategories();
  const { lecturers, fetchLecturers } = useLecturers();

  useEffect(() => {
    fetchCategories();
    fetchCourseCategories();
    fetchLecturers();
  }, [fetchCategories, fetchCourseCategories, fetchLecturers]);

  const [form, setForm] = useState(
    course
      ? {
          ...course,
          categoryId: Number(course.category_id),
          courseCategoryId: Number(course.course_category_id),
          lecturerId: Number(course.lecturer_id),
          name: course.name,
          duration: course.duration,
          mode: course.mode,
          technologies: course.technologies,
          certificate: course.certificate,
          internship: course.internship,
          price: course.price,
          image: course.image,
          status: course.status,
        }
      : {
          categoryId: "",
          courseCategoryId: "",
          lecturerId: "",
          name: "",
          duration: "",
          mode: "Online",
          technologies: "",
          certificate: "Yes",
          internship: "Yes",
          price: "",
          image: null,
          status: "Active",
        }
  );

  const [preview, setPreview] = useState(
    course?.image ? `${IMG_URL}/course/${course.image}` : null
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm({ ...form, image: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  useEffect(() => {
    if (course?.image) {
      setPreview(`${IMG_URL}/course/${course.image}`);
    }
  }, [course]);

  const handleSave = () => {
    if (!form.name || !form.duration || !form.price || !form.lecturerId || !form.courseCategoryId) {
      alert("Please fill in all required fields");
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-4xl bg-white rounded-3xl ring-1 ring-gray-200 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-extrabold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-2xl hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Select Category
              </label>
              <select
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: Number(e.target.value) })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="">Select Category</option>
                {categories.filter((type) => type.status === "Active").map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Course Category
              </label>
              <select
                value={form.courseCategoryId}
                onChange={(e) => setForm({ ...form, courseCategoryId: Number(e.target.value) })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="">Select Course Category</option>
                {courseCategories.filter((type) => type.status === "Active").map((cc) => (
                  <option key={cc.id} value={cc.id}>{cc.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Select Lecturer
              </label>
              <select
                value={form.lecturerId}
                onChange={(e) => setForm({ ...form, lecturerId: Number(e.target.value) })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="">Select Lecturer</option>
                {lecturers.filter((type) => type.status === "Active").map((lec) => (
                  <option key={lec.id} value={lec.id}>{lec.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Course Name
              </label>
              <input
                type="text"
                placeholder="Course Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Duration
              </label>
              <input
                type="text"
                placeholder="Duration"
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Mode
              </label>
              <select
                value={form.mode}
                onChange={(e) => setForm({ ...form, mode: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="Online">Online</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Offline">Offline</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Technologies
              </label>
              <input
                type="text"
                placeholder="Technologies"
                value={form.technologies}
                onChange={(e) => setForm({ ...form, technologies: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Internship
              </label>
              <select
                value={form.internship}
                onChange={(e) => setForm({ ...form, internship: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Certificate
              </label>
              <select
                value={form.certificate}
                onChange={(e) => setForm({ ...form, certificate: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Price
              </label>
              <input
                type="number"
                placeholder="Price"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Course Image
              </label>
              <div className="space-y-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-2xl file:border-0 file:text-sm file:font-extrabold file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
                />

                {preview && (
                  <div className="flex items-center gap-3">
                    <img
                      src={preview}
                      className="h-24 w-24 object-cover rounded-2xl ring-1 ring-gray-200"
                      alt="Course Preview"
                    />
                    <div className="text-sm text-gray-600">
                      Course image preview
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            className="w-full mt-6 bg-red-600 text-white py-3 rounded-2xl font-extrabold hover:bg-red-700 transition-colors shadow-sm"
          >
            Save Course
          </button>
        </div>
      </div>
    </div>
  );
}
